import React from "react";
import { HiOutlinePlus, HiOutlineTrash, HiInformationCircle } from "react-icons/hi";
import CodeLookup from "../CodeLookup";
import MutuelleCodeLookup from "../MutuelleCodeLookup";
import QuestionLookup from "../QuestionLookup";
import AnswerLookup from "../AnswerLookup";
import LaboratoryLookup from "../LaboratoryLookup";
import StatusLookup from "../StatusLookup";
import ExamTypeSelect from "../action/ExamTypeSelect";
import { PRESCRIPTION_WARNING_LABELS } from "../../utils";

const INFO_DAY_OPTIONS = [
  { value: "MONDAY", label: "Lundi" },
  { value: "TUESDAY", label: "Mardi" },
  { value: "WEDNESDAY", label: "Mercredi" },
  { value: "THURSDAY", label: "Jeudi" },
  { value: "FRIDAY", label: "Vendredi" },
  { value: "SATURDAY", label: "Samedi" },
  { value: "SUNDAY", label: "Dimanche" },
];

const TRIGGER_OPTIONS = [
  { key: "CODE_SOME", label: "Contient au moins un de ces codes", type: "CODE_SOME", field: "", category: "Prescription", defaultValues: [] },
  { key: "CODE_EVERY", label: "Contient tous ces codes", type: "CODE_EVERY", field: "", category: "Prescription", defaultValues: [] },
  { key: "EXAM_TYPE_SOME", label: "Contient au moins un de ces types", type: "EXAM_TYPE_SOME", field: "", category: "Prescription", defaultValues: [] },
  { key: "EXAM_TYPE_EVERY", label: "Contient tous ces types", type: "EXAM_TYPE_EVERY", field: "", category: "Prescription", defaultValues: [] },
  { key: "INFO_PRESCRIPTION", label: "Ordonnance jointe", type: "INFO", field: "PRESCRIPTION", category: "Prescription", defaultValues: [] },
  { key: "INFO_EXAM", label: "Analyses lues sur l'ordonnance", type: "INFO", field: "EXAM", category: "Prescription", defaultValues: [] },
  { key: "INFO_PRESCRIPTION_WARNING", label: "L'ordonnance a une de ces alertes", type: "INFO", field: "PRESCRIPTION_WARNING", category: "Prescription", defaultValues: [] },
  { key: "INFO_SEXE", label: "Sexe", type: "INFO", field: "SEXE", category: "Patient", defaultValues: [] },
  { key: "INFO_AGE", label: "Âge", type: "INFO", field: "AGE", category: "Patient", defaultValues: [undefined, undefined] },
  { key: "INFO_PATIENT_STATUS", label: "Patient", type: "INFO", field: "PATIENT_STATUS", category: "Patient", defaultValues: [] },
  { key: "INFO_INS", label: "Identité INS", type: "INFO", field: "INS", category: "Patient", defaultValues: [] },
  { key: "INFO_MUTUELLE", label: "Droits mutuelle", type: "INFO", field: "MUTUELLE", category: "Patient", defaultValues: [] },
  { key: "INFO_TYPE", label: "Lieux d'enregistrement", type: "INFO", field: "TYPE", category: "Admission", defaultValues: [] },
  { key: "MUTUELLE_CODE", label: "Mutuelle parmi…", type: "MUTUELLE_CODE", field: "", category: "Patient", defaultValues: [] },
  { key: "LABORATORY", label: "Laboratoire", type: "LABORATORY", field: "", category: "Admission", defaultValues: [] },
  { key: "INFO_DAY", label: "Jour de la soumission", type: "INFO", field: "DAY", category: "Admission", defaultValues: [] },
  { key: "INFO_HOUR", label: "Heure de la soumission", type: "INFO", field: "HOUR", category: "Admission", defaultValues: [undefined, undefined] },
  { key: "QUESTION", label: "Réponse à une question", type: "QUESTION", field: "", category: "Questionnaire", defaultValues: [] },
  { key: "FLAG", label: "Alerte sur le dossier", type: "FLAG", field: "", category: "Admission", defaultValues: [] },
  { key: "STATUS", label: "Statut du dossier", type: "STATUS", field: "", category: "Admission", defaultValues: [] },
  { key: "SOURCE", label: "Saisie par", type: "SOURCE", field: "", category: "Admission", defaultValues: [] },
  {
    key: "ADMISSION_COUNT",
    label: "Rang dans la journée (même patient)",
    type: "ADMISSION_COUNT",
    field: "SAME_DAY",
    category: "Admission",
    defaultValues: [">=", ""],
  },
  { key: "DELTA", label: "Écart entre deux moments", type: "DELTA", field: "HOUR", category: "Admission", defaultValues: ["INFO", "INFO", ">=", ""] },
  { key: "ALWAYS", label: "Toujours (sans condition)", type: "ALWAYS", field: "", category: "Avancé", defaultValues: [] },
];

const TRIGGER_CATEGORIES = ["Prescription", "Patient", "Admission", "Questionnaire", "Avancé"];

const encodeTriggerKey = (type, field) => {
  if (type === "INFO" && field) return `INFO_${field}`;
  return type || "CODE_SOME";
};

const decodeTriggerKey = (key) => {
  const option = TRIGGER_OPTIONS.find((opt) => opt.key === key);
  if (!option) return { type: "CODE_SOME", field: "", values: [] };
  return { type: option.type, field: option.field, values: [...option.defaultValues] };
};

const parseDeltaSource = (raw) => {
  if (!raw || raw === "INFO") return { kind: "INFO", questionId: null };
  if (raw.startsWith("QUESTION:")) return { kind: "QUESTION", questionId: raw.slice(9) || null };
  return { kind: "INFO", questionId: null };
};

const encodeDeltaSource = (kind, questionId) => {
  if (kind === "QUESTION") return questionId ? `QUESTION:${questionId}` : "QUESTION:";
  return "INFO";
};

const DeltaSourceSelect = ({ label, source, deltaField, onChangeKind, onChangeQuestion }) => (
  <div className="mb-2">
    <div className="flex items-center space-x-2">
      <span className="whitespace-nowrap text-sm text-gray-500">{label}</span>
      <select className="w-full rounded-md border p-2" value={source.kind} onChange={(e) => onChangeKind(e.target.value)}>
        <option value="INFO">{deltaField === "DATE" ? "Date" : "Heure"} de la soumission</option>
        <option value="QUESTION">Réponse à une question ({deltaField === "DATE" ? "date" : "heure"})</option>
      </select>
    </div>
    {source.kind === "QUESTION" && (
      <div className="mt-1">
        <QuestionLookup onChange={(e) => onChangeQuestion(e?._id)} initialValue={source.questionId} answerType={deltaField} />
      </div>
    )}
  </div>
);

const DATETIME_UNIT_OPTIONS = [
  { value: "MINUTES", label: "min" },
  { value: "HOURS", label: "h" },
  { value: "DAYS", label: "j" },
];

const DeltaDatetimeFields = ({ trigger, onChange }) => {
  const deltaValues = trigger.values?.length >= 7 ? trigger.values : ["INFO", "INFO", "INFO", "INFO", ">=", "", "MINUTES"];
  const dateSourceA = parseDeltaSource(deltaValues[0]);
  const hourSourceA = parseDeltaSource(deltaValues[1]);
  const dateSourceB = parseDeltaSource(deltaValues[2]);
  const hourSourceB = parseDeltaSource(deltaValues[3]);
  const operator = deltaValues[4] || ">=";
  const threshold = deltaValues[5] || "";
  const unit = deltaValues[6] || "MINUTES";

  const updateValues = (index, value) => {
    const next = [...deltaValues];
    next[index] = value;
    onChange({ values: next });
  };

  return (
    <>
      <div className="mb-2">
        <span className="text-sm font-medium text-gray-700">Départ</span>
        <DeltaSourceSelect
          label="Date"
          source={dateSourceA}
          deltaField="DATE"
          onChangeKind={(kind) => updateValues(0, encodeDeltaSource(kind, null))}
          onChangeQuestion={(id) => updateValues(0, encodeDeltaSource("QUESTION", id))}
        />
        <DeltaSourceSelect
          label="Heure"
          source={hourSourceA}
          deltaField="HOUR"
          onChangeKind={(kind) => updateValues(1, encodeDeltaSource(kind, null))}
          onChangeQuestion={(id) => updateValues(1, encodeDeltaSource("QUESTION", id))}
        />
      </div>
      <div className="mb-2">
        <span className="text-sm font-medium text-gray-700">Arrivée</span>
        <DeltaSourceSelect
          label="Date"
          source={dateSourceB}
          deltaField="DATE"
          onChangeKind={(kind) => updateValues(2, encodeDeltaSource(kind, null))}
          onChangeQuestion={(id) => updateValues(2, encodeDeltaSource("QUESTION", id))}
        />
        <DeltaSourceSelect
          label="Heure"
          source={hourSourceB}
          deltaField="HOUR"
          onChangeKind={(kind) => updateValues(3, encodeDeltaSource(kind, null))}
          onChangeQuestion={(id) => updateValues(3, encodeDeltaSource("QUESTION", id))}
        />
      </div>
      <div className="flex items-center space-x-2">
        <select className="w-full rounded-md border p-2" value={operator} onChange={(e) => updateValues(4, e.target.value)}>
          <option value=">=">Au moins</option>
          <option value="<=">Au plus</option>
        </select>
        <input type="number" className="w-full rounded-md border p-2" placeholder="Durée" value={threshold} onChange={(e) => updateValues(5, e.target.value)} min="0" />
        <select className="w-24 flex-shrink-0 rounded-md border p-2" value={unit} onChange={(e) => updateValues(6, e.target.value)}>
          {DATETIME_UNIT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
      <p className="mt-1.5 text-xs italic text-gray-500">{"Écart = arrivée − départ ; négatif si l'arrivée est avant le départ."}</p>
    </>
  );
};

const DeltaTriggerFields = ({ trigger, onChange }) => {
  const deltaField = trigger.field || "HOUR";

  if (deltaField === "DATETIME") {
    return (
      <>
        <div className="mb-2 flex items-center justify-between gap-4">
          <select
            className="w-full rounded-md border p-2"
            value={deltaField}
            onChange={(e) => {
              const newField = e.target.value;
              if (newField === "DATETIME") {
                onChange({ field: newField, values: ["INFO", "INFO", "INFO", "INFO", ">=", "", "MINUTES"] });
              } else {
                onChange({ field: newField, values: ["INFO", "INFO", ">=", ""] });
              }
            }}
          >
            <option value="HOUR">Écart horaire (en minutes)</option>
            <option value="DATE">Écart en jours</option>
            <option value="DATETIME">Écart date + heure</option>
          </select>
        </div>
        <DeltaDatetimeFields trigger={trigger} onChange={onChange} />
      </>
    );
  }

  const deltaValues = trigger.values?.length >= 4 ? trigger.values : ["INFO", "INFO", ">=", ""];
  const sourceA = parseDeltaSource(deltaValues[0]);
  const sourceB = parseDeltaSource(deltaValues[1]);
  const operator = deltaValues[2] || ">=";
  const threshold = deltaValues[3] || "";

  const updateValues = (index, value) => {
    const next = [...deltaValues];
    next[index] = value;
    onChange({ values: next });
  };

  return (
    <>
      <div className="mb-2 flex items-center justify-between gap-4">
        <select
          className="w-full rounded-md border p-2"
          value={deltaField}
          onChange={(e) => {
            const newField = e.target.value;
            if (newField === "DATETIME") {
              onChange({ field: newField, values: ["INFO", "INFO", "INFO", "INFO", ">=", "", "MINUTES"] });
            } else {
              onChange({ field: newField, values: ["INFO", "INFO", operator, threshold] });
            }
          }}
        >
          <option value="HOUR">Écart horaire (en minutes)</option>
          <option value="DATE">Écart en jours</option>
          <option value="DATETIME">Écart date + heure</option>
        </select>
      </div>

      <DeltaSourceSelect
        label="Départ"
        source={sourceA}
        deltaField={deltaField}
        onChangeKind={(kind) => updateValues(0, encodeDeltaSource(kind, null))}
        onChangeQuestion={(id) => updateValues(0, encodeDeltaSource("QUESTION", id))}
      />
      <DeltaSourceSelect
        label="Arrivée"
        source={sourceB}
        deltaField={deltaField}
        onChangeKind={(kind) => updateValues(1, encodeDeltaSource(kind, null))}
        onChangeQuestion={(id) => updateValues(1, encodeDeltaSource("QUESTION", id))}
      />

      <div className="flex items-center space-x-2">
        <select className="w-full rounded-md border p-2" value={operator} onChange={(e) => updateValues(2, e.target.value)}>
          <option value=">=">Au moins</option>
          <option value="<=">Au plus</option>
        </select>
        <input type="number" className="w-full rounded-md border p-2" placeholder="Durée" value={threshold} onChange={(e) => updateValues(3, e.target.value)} min="0" />
        <span className="whitespace-nowrap text-sm text-gray-500">{deltaField === "DATE" ? "jours" : "min"}</span>
      </div>
      <p className="mt-1.5 text-xs italic text-gray-500">{"Écart = arrivée − départ ; négatif si l'arrivée est avant le départ."}</p>
    </>
  );
};

const Trigger = ({ groupsOperator, setGroupsOperator, triggerGroups, setTriggerGroups, codesIncludeRelated = false }) => {
  const updateGroup = (index, updates) => {
    setTriggerGroups((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...updates };
      return next;
    });
  };

  const updateTrigger = (groupIndex, triggerIndex, updates) => {
    setTriggerGroups((prev) => {
      const next = [...prev];
      const group = next[groupIndex] || { operator: "AND", triggers: [] };
      const triggers = [...(group.triggers || [])];
      triggers[triggerIndex] = { ...triggers[triggerIndex], ...updates };

      next[groupIndex] = { ...group, triggers };
      return next;
    });
  };

  return (
    <div>
      <div className="mb-2 text-sm font-medium text-gray-700">Se déclenche quand…</div>
      {triggerGroups.length > 1 && (
        <p className="mb-2 text-xs italic text-gray-500">{groupsOperator === "OR" ? "Au moins un des groupes doit être vrai." : "Tous les groupes doivent être vrais."}</p>
      )}

      {triggerGroups.map((group, groupIndex) => (
        <div key={groupIndex}>
          {groupIndex !== 0 && (
            <div className="my-3 flex items-center gap-2">
              <div className="h-px flex-1 bg-gray-200" />
              <div className="inline-flex overflow-hidden rounded-md border">
                <button
                  type="button"
                  className={`px-3 py-0.5 text-xs ${groupsOperator === "AND" ? "bg-primary-500 text-white" : "bg-white text-gray-700"}`}
                  title="Tous les groupes doivent être vrais"
                  onClick={() => setGroupsOperator("AND")}
                >
                  ET
                </button>
                <button
                  type="button"
                  className={`border-l px-3 py-0.5 text-xs ${groupsOperator === "OR" ? "bg-primary-500 text-white" : "bg-white text-gray-700"}`}
                  title="Au moins un groupe doit être vrai"
                  onClick={() => setGroupsOperator("OR")}
                >
                  OU
                </button>
              </div>
              <div className="h-px flex-1 bg-gray-200" />
            </div>
          )}
          <div className="mt-1 rounded-lg border border-l-4 border-primary-200 bg-white p-4">
            <div className="mb-3 flex items-center justify-between">
              <div className="rounded-full border border-primary-100 bg-primary-50 px-2 py-0.5 text-xs text-primary-700">Groupe {groupIndex + 1}</div>
              {triggerGroups.length > 1 && (
                <button type="button" className="text-red-500" onClick={() => setTriggerGroups((prev) => prev.filter((_, i) => i !== groupIndex))}>
                  <HiOutlineTrash />
                </button>
              )}
            </div>

            {(group.triggers || []).map((trigger, index) => (
              <div key={index}>
                {index !== 0 && (
                  <div className="mt-2 flex items-center justify-center">
                    <div className="inline-flex overflow-hidden rounded-md border">
                      <button
                        type="button"
                        className={`px-3 py-0.5 text-xs ${(group.operator || "AND") === "AND" ? "bg-primary-500 text-white" : "bg-white text-gray-700"}`}
                        title="Toutes les conditions doivent être vraies"
                        onClick={() => updateGroup(groupIndex, { operator: "AND" })}
                      >
                        ET
                      </button>
                      <button
                        type="button"
                        className={`border-l px-3 py-0.5 text-xs ${(group.operator || "AND") === "OR" ? "bg-primary-500 text-white" : "bg-white text-gray-700"}`}
                        title="Au moins une condition doit être vraie"
                        onClick={() => updateGroup(groupIndex, { operator: "OR" })}
                      >
                        OU
                      </button>
                    </div>
                  </div>
                )}
                <div className="mt-1 rounded-lg border p-4">
                  <div className="mb-2 flex items-center gap-2">
                    <select
                      className="min-w-0 flex-1 rounded-md border p-2"
                      value={encodeTriggerKey(trigger.type, trigger.field)}
                      onChange={(e) => {
                        const { type, field, values } = decodeTriggerKey(e.target.value);
                        updateTrigger(groupIndex, index, { type, field, values, negate: false });
                      }}
                    >
                      {TRIGGER_CATEGORIES.map((category) => (
                        <optgroup key={category} label={category}>
                          {TRIGGER_OPTIONS.filter((opt) => opt.category === category).map((opt) => (
                            <option key={opt.key} value={opt.key}>
                              {opt.label}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    {trigger.type !== "ALWAYS" && (
                      <select
                        className="rounded-md border p-2"
                        value={trigger.negate ? "not" : "is"}
                        onChange={(e) => updateTrigger(groupIndex, index, { negate: e.target.value === "not" })}
                      >
                        <option value="is">Si</option>
                        <option value="not">Sauf si</option>
                      </select>
                    )}
                    {(group.triggers || []).length > 1 && (
                      <button
                        type="button"
                        className="flex-shrink-0 text-red-500"
                        onClick={() =>
                          setTriggerGroups((prev) => {
                            const next = [...prev];
                            next[groupIndex] = {
                              ...next[groupIndex],
                              triggers: (next[groupIndex].triggers || []).filter((_, i) => i !== index),
                            };
                            return next;
                          })
                        }
                      >
                        <HiOutlineTrash />
                      </button>
                    )}
                  </div>

                  {(trigger.type === "CODE_SOME" || trigger.type === "CODE_EVERY") && (
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Codes</label>
                      <CodeLookup onChange={(tags) => updateTrigger(groupIndex, index, { values: tags.map((t) => t.value) })} initialValues={trigger.values} />
                      {codesIncludeRelated === true && (
                        <p className="mt-1.5 flex items-center gap-1.5 text-xs italic text-gray-500">
                          <HiInformationCircle className="h-3.5 w-3.5 text-gray-400" />
                          Inclut aussi les sous-codes associés
                        </p>
                      )}
                    </div>
                  )}

                  {(trigger.type === "EXAM_TYPE_SOME" || trigger.type === "EXAM_TYPE_EVERY") && (
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Types d'analyse</label>
                      <ExamTypeSelect value={trigger.values || []} onChange={(values) => updateTrigger(groupIndex, index, { values })} />
                    </div>
                  )}

                  {trigger.type === "MUTUELLE_CODE" && (
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Mutuelles concernées</label>
                      <MutuelleCodeLookup onChange={(values) => updateTrigger(groupIndex, index, { values })} initialValues={trigger.values} />
                    </div>
                  )}

                  {trigger.type === "QUESTION" && (
                    <>
                      <div className="mb-2">
                        <label className="mb-1 block text-sm font-medium text-gray-700">Question</label>
                        <QuestionLookup onChange={(e) => updateTrigger(groupIndex, index, { field: e?._id })} initialValue={trigger.field} />
                      </div>
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">Réponses attendues</label>
                        <AnswerLookup
                          onChange={(e) => updateTrigger(groupIndex, index, { values: e.map((v) => v._id) })}
                          questionId={trigger.field}
                          initialValue={trigger.values}
                          placeholder="Au moins une de ces réponses"
                        />
                      </div>
                    </>
                  )}

                  {trigger.type === "INFO" && trigger.field === "SEXE" && (
                    <div className="mb-2">
                      <select className="w-full rounded-md border p-2" value={trigger.values?.[0]} onChange={(e) => updateTrigger(groupIndex, index, { values: [e.target.value] })}>
                        <option value="">Sélectionner</option>
                        <option value="M">Homme</option>
                        <option value="F">Femme</option>
                      </select>
                    </div>
                  )}
                  {trigger.type === "INFO" && trigger.field === "INS" && (
                    <div className="mb-2">
                      <select className="w-full rounded-md border p-2" value={trigger.values?.[0]} onChange={(e) => updateTrigger(groupIndex, index, { values: [e.target.value] })}>
                        <option value="">Sélectionner</option>
                        <option value="QUALIFIED">Qualifiée</option>
                        <option value="NON_QUALIFIED">Non qualifiée</option>
                      </select>
                    </div>
                  )}
                  {trigger.type === "INFO" && trigger.field === "MUTUELLE" && (
                    <div className="mb-2">
                      <select className="w-full rounded-md border p-2" value={trigger.values?.[0]} onChange={(e) => updateTrigger(groupIndex, index, { values: [e.target.value] })}>
                        <option value="">Sélectionner</option>
                        <option value="UP_TO_DATE">Droits à jour</option>
                        <option value="OUTDATED">Droits expirés ou sans date de fin</option>
                        <option value="UNKNOWN">Aucune mutuelle renseignée</option>
                      </select>
                    </div>
                  )}
                  {trigger.type === "INFO" && trigger.field === "PRESCRIPTION" && (
                    <div className="mb-2">
                      <select className="w-full rounded-md border p-2" value={trigger.values?.[0]} onChange={(e) => updateTrigger(groupIndex, index, { values: [e.target.value] })}>
                        <option value="">Sélectionner</option>
                        <option value="PRESENT">Au moins une ordonnance</option>
                        <option value="ABSENT">Aucune ordonnance</option>
                      </select>
                    </div>
                  )}
                  {trigger.type === "INFO" && trigger.field === "EXAM" && (
                    <div className="mb-2">
                      <select className="w-full rounded-md border p-2" value={trigger.values?.[0]} onChange={(e) => updateTrigger(groupIndex, index, { values: [e.target.value] })}>
                        <option value="">Sélectionner</option>
                        <option value="DETECTED">Analyses détectées sur au moins une ordonnance</option>
                        <option value="NOT_DETECTED">Au moins une ordonnance sans aucune analyse détectée</option>
                      </select>
                    </div>
                  )}
                  {trigger.type === "INFO" && trigger.field === "PRESCRIPTION_WARNING" && (
                    <div>
                      <div className="flex flex-wrap gap-x-4 gap-y-2">
                        {Object.entries(PRESCRIPTION_WARNING_LABELS).map(([warning, label]) => (
                          <label key={warning} className="flex items-center gap-2 text-sm text-gray-700">
                            <input
                              type="checkbox"
                              checked={(trigger.values || []).includes(warning)}
                              onChange={(e) => {
                                const nextValues = new Set(trigger.values || []);
                                if (e.target.checked) nextValues.add(warning);
                                else nextValues.delete(warning);
                                updateTrigger(groupIndex, index, { values: [...nextValues] });
                              }}
                            />
                            {label}
                          </label>
                        ))}
                      </div>
                      <p className="mt-1.5 text-xs italic text-gray-500">{"Alertes posées à la lecture automatique de l'ordonnance ; une correction manuelle ne les retire pas."}</p>
                    </div>
                  )}
                  {trigger.type === "INFO" && trigger.field === "PATIENT_STATUS" && (
                    <div className="mb-2">
                      <select className="w-full rounded-md border p-2" value={trigger.values?.[0]} onChange={(e) => updateTrigger(groupIndex, index, { values: [e.target.value] })}>
                        <option value="">Sélectionner</option>
                        <option value="KNOWN">Déjà connu du SIL</option>
                        <option value="UNKNOWN">Nouveau patient</option>
                      </select>
                    </div>
                  )}
                  {trigger.type === "INFO" && trigger.field === "AGE" && (
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-500">Entre</span>
                      <input
                        type="number"
                        className="w-full rounded-md border p-2"
                        placeholder="Min"
                        value={trigger.values?.[0] || ""}
                        onChange={(e) => updateTrigger(groupIndex, index, { values: [e.target.value, trigger.values?.[1]] })}
                        min="0"
                        max="120"
                      />
                      <span className="text-gray-500">et</span>
                      <input
                        type="number"
                        className="w-full rounded-md border p-2"
                        placeholder="Max"
                        value={trigger.values?.[1] || ""}
                        onChange={(e) => updateTrigger(groupIndex, index, { values: [trigger.values?.[0], e.target.value] })}
                        min="0"
                        max="120"
                      />
                      <span className="text-gray-500">ans</span>
                    </div>
                  )}
                  {trigger.type === "INFO" && trigger.field === "TYPE" && (
                    <div className="mb-2">
                      <select className="w-full rounded-md border p-2" value={trigger.values?.[0]} onChange={(e) => updateTrigger(groupIndex, index, { values: [e.target.value] })}>
                        <option value="">Sélectionner</option>
                        <option value="NOW">Au laboratoire</option>
                        <option value="LATER">À domicile</option>
                      </select>
                    </div>
                  )}
                  {trigger.type === "INFO" && trigger.field === "DAY" && (
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Jours concernés</label>
                      <div className="flex flex-wrap gap-2">
                        {INFO_DAY_OPTIONS.map((day) => {
                          const isSelected = (trigger.values || []).includes(day.value);
                          return (
                            <label key={day.value} className="flex items-center gap-2 text-sm text-gray-700">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  const nextValues = new Set(trigger.values || []);
                                  if (e.target.checked) nextValues.add(day.value);
                                  else nextValues.delete(day.value);
                                  updateTrigger(groupIndex, index, { values: [...nextValues] });
                                }}
                              />
                              {day.label}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {trigger.type === "INFO" && trigger.field === "HOUR" && (
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="text-gray-500">Entre</span>
                        <input
                          type="time"
                          step="60"
                          className="w-full rounded-md border p-2"
                          value={trigger.values?.[0] || ""}
                          onChange={(e) => updateTrigger(groupIndex, index, { values: [e.target.value, trigger.values?.[1]] })}
                        />
                        <span className="text-gray-500">et</span>
                        <input
                          type="time"
                          step="60"
                          className="w-full rounded-md border p-2"
                          value={trigger.values?.[1] || ""}
                          onChange={(e) => updateTrigger(groupIndex, index, { values: [trigger.values?.[0], e.target.value] })}
                        />
                      </div>
                      <p className="mt-1.5 text-xs italic text-gray-500">Une plage qui passe minuit (ex. 22:00 à 06:00) ne correspond jamais.</p>
                    </div>
                  )}

                  {trigger.type === "FLAG" && (
                    <div className="mb-2">
                      <select
                        className="w-full rounded-md border p-2"
                        value={trigger.values?.[0] || ""}
                        onChange={(e) => updateTrigger(groupIndex, index, { values: [e.target.value] })}
                      >
                        <option value="">Sélectionner</option>
                        <option value="PATIENT_INFO_ALREADY_EXISTS">Patient déjà existant</option>
                        <option value="PATIENT_NAME_MISSING_IN_PRESCRIPTION">{"Nom du patient absent de l'ordonnance"}</option>
                        <option value="NOT_ELIGIBLE">Dossier non éligible</option>
                        <option value="DUPLICATE">Dossier en doublon</option>
                      </select>
                    </div>
                  )}

                  {trigger.type === "STATUS" && (
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Un de ces statuts</label>
                      <StatusLookup onChange={(values) => updateTrigger(groupIndex, index, { values })} initialValues={trigger.values} />
                    </div>
                  )}

                  {trigger.type === "LABORATORY" && (
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Laboratoires</label>
                      <LaboratoryLookup onChange={(values) => updateTrigger(groupIndex, index, { values })} initialValues={trigger.values} />
                    </div>
                  )}

                  {trigger.type === "SOURCE" && (
                    <div className="mb-2">
                      <select
                        className="w-full rounded-md border p-2"
                        value={trigger.values?.[0] || ""}
                        onChange={(e) => updateTrigger(groupIndex, index, { values: [e.target.value] })}
                      >
                        <option value="">Sélectionner</option>
                        <option value="PATIENT">Le patient (appli patient)</option>
                        <option value="IDE">{"L'infirmier (appli IDE)"}</option>
                      </select>
                    </div>
                  )}

                  {trigger.type === "ADMISSION_COUNT" && (
                    <div className="flex items-center space-x-2">
                      <select
                        className="w-full rounded-md border p-2"
                        value={trigger.values?.[0] || ">="}
                        onChange={(e) => updateTrigger(groupIndex, index, { values: [e.target.value, trigger.values?.[1] ?? ""] })}
                      >
                        <option value=">=">{"C'est au moins la"}</option>
                        <option value="==">{"C'est exactement la"}</option>
                        <option value="<=">{"C'est au plus la"}</option>
                      </select>
                      <input
                        type="number"
                        min="1"
                        className="w-16 flex-shrink-0 rounded-md border p-2"
                        placeholder="N°"
                        value={trigger.values?.[1] ?? ""}
                        onChange={(e) => updateTrigger(groupIndex, index, { values: [trigger.values?.[0] || ">=", e.target.value] })}
                      />
                      <span className="whitespace-nowrap text-sm text-gray-500">{Number(trigger.values?.[1]) === 1 ? "ʳᵉ" : "ᵉ"} admission du patient</span>
                    </div>
                  )}

                  {trigger.type === "DELTA" && <DeltaTriggerFields trigger={trigger} onChange={(updates) => updateTrigger(groupIndex, index, updates)} />}
                </div>
              </div>
            ))}

            <div className="mt-2 flex justify-end">
              <button
                type="button"
                className="flex items-center text-sm text-primary-500 transition-colors hover:text-primary-700"
                onClick={() =>
                  setTriggerGroups((prev) => {
                    const next = [...prev];
                    next[groupIndex] = {
                      ...next[groupIndex],
                      triggers: [...(next[groupIndex].triggers || []), { type: "CODE_SOME", values: [], field: "" }],
                    };
                    return next;
                  })
                }
              >
                <HiOutlinePlus className="mr-1" /> Ajouter une condition
              </button>
            </div>
          </div>
        </div>
      ))}

      <div className="mt-3 flex justify-end">
        <button
          type="button"
          className="flex items-center text-sm text-primary-500 transition-colors hover:text-primary-700"
          onClick={() => setTriggerGroups((prev) => [...prev, { operator: "AND", triggers: [{ type: "CODE_SOME", values: [], field: "" }] }])}
        >
          <HiOutlinePlus className="mr-1" /> {groupsOperator === "OR" ? "Ajouter un groupe alternatif" : "Ajouter un groupe"}
        </button>
      </div>
    </div>
  );
};

export default Trigger;
